import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, ChevronDown } from 'lucide-react';
import { FaViber, FaWhatsapp, FaTelegram } from 'react-icons/fa';
import { getSettings } from '../api';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    loadSettings();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const loadSettings = async () => {
    try {
      const response = await getSettings();
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const siteSettings = settings || {
    companyName: "Мандарин",
    phone: "+375 29 186-13-40",
    messengers: {
      viber: "viber://chat?number=%2B375291861340",
      whatsapp: "https://wa.me/375291861340",
      telegram: "https://t.me/+375291861340"
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Уборка', hasDropdown: true },
    { label: 'Химчистка', path: '/himchistka' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-3' : 'bg-white/95 py-4'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="relative w-12 h-12">
              <img
                src="/logo.png"
                alt="Mandarin Cleaning Company"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="text-xl font-bold text-gray-900">{siteSettings.companyName}</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <div className="relative group">
              <button
                className="flex items-center space-x-1 text-gray-700 hover:text-orange-600 transition-colors font-medium"
                onMouseEnter={() => setIsServicesOpen(true)}
                onMouseLeave={() => setIsServicesOpen(false)}
              >
                <span>Уборка</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {isServicesOpen && (
                <div
                  className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 border border-gray-100"
                  onMouseEnter={() => setIsServicesOpen(true)}
                  onMouseLeave={() => setIsServicesOpen(false)}
                >
                  <Link
                    to="/podderzhivayushhaya-uborka"
                    className="block px-4 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                  >
                    Поддерживающая уборка
                  </Link>
                  <Link
                    to="/generalnaya-uborka"
                    className="block px-4 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                  >
                    Генеральная уборка
                  </Link>
                  <Link
                    to="/uborka-posle-remonta"
                    className="block px-4 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                  >
                    Уборка после ремонта
                  </Link>
                  <Link
                    to="/uborka-kuhni"
                    className="block px-4 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                  >
                    Уборка кухни
                  </Link>
                  <Link
                    to="/uborka-sanuzla"
                    className="block px-4 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                  >
                    Уборка санузла
                  </Link>
                </div>
              )}
            </div>
            <Link
              to="/himchistka"
              className="text-gray-700 hover:text-orange-600 transition-colors font-medium"
            >
              Химчистка
            </Link>
          </nav>

          {/* Contact Info */}
          <div className="hidden lg:flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <a
                href={siteSettings.messengers.viber}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center bg-purple-500 hover:bg-purple-600 text-white rounded-full transition-colors"
              >
                <FaViber className="w-5 h-5" />
              </a>
              <a
                href={siteSettings.messengers.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors"
              >
                <FaWhatsapp className="w-5 h-5" />
              </a>
              <a
                href={siteSettings.messengers.telegram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-colors"
              >
                <FaTelegram className="w-5 h-5" />
              </a>
            </div>
            <a
              href={`tel:${siteSettings.phone}`}
              className="flex items-center space-x-2 text-gray-900 hover:text-orange-600 transition-colors font-semibold"
            >
              <Phone className="w-5 h-5" />
              <span>{siteSettings.phone}</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-gray-700 hover:text-orange-600 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t pt-4">
            <nav className="space-y-3">
              <div>
                <div className="text-gray-700 font-medium mb-2">Уборка</div>
                <div className="pl-4 space-y-2">
                  <Link
                    to="/podderzhivayushhaya-uborka"
                    className="block text-gray-600 hover:text-orange-600 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Поддерживающая уборка
                  </Link>
                  <Link
                    to="/generalnaya-uborka"
                    className="block text-gray-600 hover:text-orange-600 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Генеральная уборка
                  </Link>
                  <Link
                    to="/uborka-posle-remonta"
                    className="block text-gray-600 hover:text-orange-600 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Уборка после ремонта
                  </Link>
                  <Link
                    to="/uborka-kuhni"
                    className="block text-gray-600 hover:text-orange-600 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Уборка кухни
                  </Link>
                  <Link
                    to="/uborka-sanuzla"
                    className="block text-gray-600 hover:text-orange-600 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Уборка санузла
                  </Link>
                </div>
              </div>
              <Link
                to="/himchistka"
                className="block text-gray-700 hover:text-orange-600 transition-colors font-medium"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Химчистка
              </Link>
            </nav>
            <div className="mt-4 pt-4 border-t">
              <a
                href={`tel:${siteSettings.phone}`}
                className="flex items-center space-x-2 text-gray-900 font-semibold mb-3"
              >
                <Phone className="w-5 h-5" />
                <span>{siteSettings.phone}</span>
              </a>
              <div className="flex items-center space-x-2">
                <a
                  href={siteSettings.messengers.viber}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center bg-purple-500 hover:bg-purple-600 text-white rounded-full transition-colors"
                >
                  <FaViber className="w-5 h-5" />
                </a>
                <a
                  href={siteSettings.messengers.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5" />
                </a>
                <a
                  href={siteSettings.messengers.telegram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-colors"
                >
                  <FaTelegram className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;